import { queryOptions, useQuery } from '@tanstack/react-query';
import { HydraUIAPIError } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import type { QueryConfig } from '../shared/react-query-types';
import { dataOutIntegrationKeys, parseDataOutIntegration, type DataOutIntegration } from './entity';

/**
 * Data-out connector reads.
 *
 * `staleTime: 0` — the record is mutated by its own config page and read back by
 * the integrations directory and connections overview, so a revisit must never
 * serve a stale copy.
 */

const STALE_TIME = 0;

/**
 * @public The connector's config, or `null` when it has never been configured.
 *
 * **A 404 is the "not yet configured" answer, not a failure.** The backend has
 * no record until the connector is first saved, so the config page seeds a
 * blank form from the catalog's settings schema on `null`. Every other status
 * is a real error and propagates — the directory and connections fan-outs read
 * `data` and already degrade to "not enabled" without it, while the config page
 * can tell "not configured" from "we could not load it" and avoid offering a
 * create that would collide with an existing record.
 */
export async function getDataOutIntegration(
  workspaceId: number,
  identifier: string,
): Promise<DataOutIntegration | null> {
  try {
    const data = await hydraUiApi.integrations.getDataOut({
      params: { path: { environment_id: String(workspaceId), identifier } },
    });
    return data.data_out == null ? null : parseDataOutIntegration(data.data_out);
  } catch (error) {
    if (error instanceof HydraUIAPIError && error.status === 404) {
      return null;
    }
    throw error;
  }
}

/** @public */
export const getDataOutIntegrationQueryOptions = (workspaceId: number, identifier: string) =>
  queryOptions({
    queryKey: dataOutIntegrationKeys.detail(workspaceId, identifier),
    queryFn: () => getDataOutIntegration(workspaceId, identifier),
    staleTime: STALE_TIME,
  });

/**
 * @public The same read, for the status fan-outs (integrations directory,
 * connections overview) rather than the config page.
 *
 * Identical `queryKey` **and** `queryFn` — one cache slot and one fetch per
 * identifier, shared with the config page — differing only in `retry: false`.
 * Keep it that way: two option factories on one key that disagree about how to
 * *fetch* would resolve to whichever observer registered last.
 *
 * The fan-outs fold every connector's `isLoading` into a single page-level
 * loader (`features/integrations/connections/sources.ts`), so a 5xx running the
 * default three-attempt backoff would hold the whole Connections tab on a
 * spinner over one optional record. Failing fast keeps the error surfaced and
 * leaves `data` undefined, which the fan-outs already read as "not enabled" —
 * the behaviour they had when this read swallowed every error, minus the
 * swallowing. The config page keeps retrying, since there the record *is* the
 * page.
 */
export const getDataOutIntegrationStatusQueryOptions = (workspaceId: number, identifier: string) =>
  queryOptions({
    ...getDataOutIntegrationQueryOptions(workspaceId, identifier),
    retry: false,
  });

/**
 * @public Whether the connector is configured — selected off the shared read.
 * A fan-out (the directory's "connected" gate), so it inherits `retry: false`.
 */
export const getDataOutIntegrationEnabledQueryOptions = (workspaceId: number, identifier: string) =>
  queryOptions({
    ...getDataOutIntegrationStatusQueryOptions(workspaceId, identifier),
    select: (data: DataOutIntegration | null) => data != null,
  });

interface UseDataOutIntegrationOptions {
  identifier: string;
  queryConfig?: QueryConfig<typeof getDataOutIntegrationQueryOptions>;
  /** The workspace to read, resolved by the caller rather than parsed from the URL. */
  workspaceId: number;
}

/**
 * @public Non-suspense: the config page branches on `isPending` itself so the
 * settings form is not interactive against an empty record, matching how the
 * Ember route blocked its template on `model()`.
 */
export const useDataOutIntegration = ({ identifier, workspaceId, queryConfig }: UseDataOutIntegrationOptions) =>
  useQuery({ ...getDataOutIntegrationQueryOptions(workspaceId, identifier), ...queryConfig });
